import React from 'react';
import { Layout, Menu, Tooltip } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import { ThunderboltFilled } from '@ant-design/icons';
import useAuthStore from '../../store/authStore';

// Custom SVG icons (user-provided in tenant/client/src/assets/icons/svg2)
import dashboardIcon from '../../assets/icons/svg2/013-laptop.svg';
import leadsIcon from '../../assets/icons/svg2/005-customer.svg';
import dealsIcon from '../../assets/icons/svg2/025-exchange.svg';
import meetingsIcon from '../../assets/icons/svg2/028-video-call.svg';
import tasksIcon from '../../assets/icons/svg2/004-clipboard.svg';
import analyticsIcon from '../../assets/icons/svg2/039-rating.svg';
import reportsIcon from '../../assets/icons/svg2/003-file.svg';
import adminIcon from '../../assets/icons/svg2/027-man-2.svg';
import profileIcon from '../../assets/icons/svg2/023-man.svg';
import settingsIcon from '../../assets/icons/svg2/010-settings.svg';
import usersIcon from '../../assets/icons/svg2/042-woman.svg';

const { Sider } = Layout;

// Render a custom svg icon as a white-tinted image. Using brightness(0)+invert(1)
// + slight drop-shadow keeps icons crisp on the deep-blue gradient background.
const SvgIcon = ({ src, size = 18 }) => (
  <span
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: size,
      height: size,
      verticalAlign: 'middle',
    }}
  >
    <img
      src={src}
      alt=""
      width={size}
      height={size}
      style={{
        filter: 'brightness(0) invert(1) drop-shadow(0 1px 2px rgba(0,0,0,0.25))',
        opacity: 0.92,
      }}
    />
  </span>
);

const Sidebar = ({ collapsed }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthStore();

  const isAdmin = user?.role === 'super_admin' || user?.role === 'tenant_admin';

  const menuItems = [
    { key: '/dashboard', icon: <SvgIcon src={dashboardIcon} />, label: 'Dashboard' },
    { key: '/leads', icon: <SvgIcon src={leadsIcon} />, label: 'Leads' },
    { key: '/deals', icon: <SvgIcon src={dealsIcon} />, label: 'Deals' },
    { key: '/meetings', icon: <SvgIcon src={meetingsIcon} />, label: 'Meetings' },
    { key: '/tasks', icon: <SvgIcon src={tasksIcon} />, label: 'Tasks' },
    { key: '/analytics', icon: <SvgIcon src={analyticsIcon} />, label: 'Analytics' },
    { key: '/reports', icon: <SvgIcon src={reportsIcon} />, label: 'Reports' },
    ...(isAdmin
      ? [
          {
            key: '/admin',
            icon: <SvgIcon src={adminIcon} />,
            label: 'Admin',
            children: [
              { key: '/admin/users', icon: <SvgIcon src={usersIcon} size={16} />, label: 'Users' },
              { key: '/admin/settings', icon: <SvgIcon src={settingsIcon} size={16} />, label: 'Settings' },
            ],
          },
        ]
      : []),
    { key: '/profile', icon: <SvgIcon src={profileIcon} />, label: 'Profile' },
    { key: '/settings', icon: <SvgIcon src={settingsIcon} />, label: 'Settings' },
  ];

  const handleMenuClick = ({ key }) => {
    navigate(key);
  };

  const getSelectedKeys = () => [location.pathname];
  const getOpenKeys = () =>
    location.pathname.startsWith('/admin') ? ['/admin'] : [];

  return (
    <Sider
      trigger={null}
      collapsible
      collapsed={collapsed}
      width={256}
      collapsedWidth={80}
      style={{
        background:
          'linear-gradient(180deg, #0c1f4d 0%, #112a6b 35%, #1e40af 70%, #2563eb 100%)',
        position: 'fixed',
        height: '100vh',
        left: 0,
        top: 0,
        zIndex: 1000,
        boxShadow: '4px 0 28px -10px rgba(15, 23, 42, 0.55)',
        overflow: 'hidden',
      }}
    >
      {/* Decorative ambient glow */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(600px 240px at 100% -10%, rgba(96, 165, 250, 0.18), transparent 60%), radial-gradient(500px 300px at -20% 120%, rgba(59, 130, 246, 0.18), transparent 60%)',
          pointerEvents: 'none',
        }}
      />

      {/* Logo */}
      <div
        style={{
          height: 64,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderBottom: '1px solid rgba(255,255,255,0.10)',
          position: 'relative',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <img
            src="/qqcrm-logo.png"
            alt="QQCRM"
            style={{
              height: collapsed ? 26 : 34,
              filter: 'brightness(0) invert(1) drop-shadow(0 2px 6px rgba(59,130,246,0.4))',
              transition: 'all 0.2s ease',
            }}
          />
        </div>
      </div>

      {/* Navigation Menu */}
      <Menu
        mode="inline"
        selectedKeys={getSelectedKeys()}
        defaultOpenKeys={getOpenKeys()}
        onClick={handleMenuClick}
        items={menuItems}
        theme="dark"
        className="qq-sidebar-menu"
        style={{
          height: 'calc(100vh - 64px - 132px)',
          borderRight: 0,
          background: 'transparent',
          padding: '12px 8px',
          fontSize: 13.5,
          fontWeight: 500,
          position: 'relative',
          overflowY: 'auto',
        }}
      />

      {/* Coming-soon teaser card pinned to the bottom */}
      <div
        className="qq-sidebar-promo"
        style={{
          position: 'absolute',
          left: collapsed ? 8 : 12,
          right: collapsed ? 8 : 12,
          bottom: 16,
        }}
      >
        {collapsed ? (
          <Tooltip title="Sales Agents — coming soon" placement="right">
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: 14,
                margin: '0 auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #0ea5e9, #2563eb 60%, #1e3a8a)',
                boxShadow: '0 8px 22px -10px rgba(14, 165, 233, 0.7), inset 0 1px 0 rgba(255,255,255,0.18)',
                color: '#fff',
                cursor: 'not-allowed',
                position: 'relative',
              }}
            >
              <ThunderboltFilled style={{ fontSize: 22 }} />
              <span
                style={{
                  position: 'absolute',
                  top: -4,
                  right: -4,
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  background: '#22d3ee',
                  border: '2px solid #0c1f4d',
                  boxShadow: '0 0 8px rgba(34, 211, 238, 0.9)',
                }}
              />
            </div>
          </Tooltip>
        ) : (
          <div
            style={{
              padding: 14,
              borderRadius: 14,
              background:
                'linear-gradient(135deg, rgba(14, 165, 233, 0.18), rgba(37, 99, 235, 0.28) 60%, rgba(15, 23, 42, 0.45))',
              border: '1px solid rgba(125, 211, 252, 0.25)',
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06)',
              backdropFilter: 'blur(6px)',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* Soft glow blob */}
            <div
              aria-hidden
              style={{
                position: 'absolute',
                top: -30,
                right: -20,
                width: 90,
                height: 90,
                borderRadius: '50%',
                background:
                  'radial-gradient(closest-side, rgba(34, 211, 238, 0.4), transparent 70%)',
                filter: 'blur(4px)',
                pointerEvents: 'none',
              }}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 10,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'linear-gradient(135deg, #38bdf8, #2563eb)',
                  color: '#fff',
                  fontSize: 16,
                  boxShadow: '0 6px 14px -6px rgba(56, 189, 248, 0.7)',
                  flexShrink: 0,
                }}
              >
                <ThunderboltFilled />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: '#f1f5f9',
                    lineHeight: 1.2,
                  }}
                >
                  Sales Agents
                </div>
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 600,
                    letterSpacing: 0.8,
                    color: '#7dd3fc',
                    marginTop: 2,
                    textTransform: 'uppercase',
                  }}
                >
                  Coming soon
                </div>
              </div>
            </div>
            <div
              style={{
                fontSize: 11,
                color: 'rgba(226, 232, 240, 0.78)',
                lineHeight: 1.45,
                marginBottom: 10,
              }}
            >
              AI agents that prospect, qualify and follow up on leads — autonomously.
            </div>
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '4px 10px',
                borderRadius: 999,
                background: 'rgba(255,255,255,0.08)',
                border: '1px solid rgba(125, 211, 252, 0.3)',
                fontSize: 10.5,
                fontWeight: 600,
                color: '#bae6fd',
                letterSpacing: 0.3,
              }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  background: '#22d3ee',
                  boxShadow: '0 0 8px rgba(34, 211, 238, 0.9)',
                  animation: 'qq-promo-pulse 1.6s ease-in-out infinite',
                }}
              />
              Notify me when launched
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes qq-promo-pulse {
          0%, 100% { opacity: 0.55; }
          50% { opacity: 1; }
        }
      `}</style>

      {/* Inline override: lift selected item with translucent gradient pill */}
      <style>{`
        .qq-sidebar-menu.ant-menu-dark,
        .qq-sidebar-menu.ant-menu-dark .ant-menu-sub {
          background: transparent !important;
        }
        .qq-sidebar-menu .ant-menu-item,
        .qq-sidebar-menu .ant-menu-submenu-title {
          border-radius: 10px !important;
          margin: 4px 0 !important;
          height: 44px !important;
          line-height: 44px !important;
          color: rgba(226, 232, 240, 0.85) !important;
          transition: background 0.18s ease, color 0.18s ease, transform 0.18s ease;
          display: flex !important;
          align-items: center !important;
          gap: 18px !important;
        }
        /* Reset antd icon margin so only flex gap controls spacing */
        .qq-sidebar-menu .ant-menu-item .ant-menu-item-icon,
        .qq-sidebar-menu .ant-menu-submenu-title .ant-menu-item-icon,
        .qq-sidebar-menu .ant-menu-item > .anticon,
        .qq-sidebar-menu .ant-menu-submenu-title > .anticon {
          margin: 0 !important;
          font-size: 18px !important;
          flex-shrink: 0 !important;
        }
        .qq-sidebar-menu:not(.ant-menu-inline-collapsed) .ant-menu-item,
        .qq-sidebar-menu:not(.ant-menu-inline-collapsed) .ant-menu-submenu-title {
          padding-left: 20px !important;
          padding-right: 16px !important;
        }
        .qq-sidebar-menu:not(.ant-menu-inline-collapsed) .ant-menu-title-content {
          margin-left: 4px !important;
          flex: 1;
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        /* Collapsed: center icon, hide label and submenu arrow completely */
        .qq-sidebar-menu.ant-menu-inline-collapsed .ant-menu-item,
        .qq-sidebar-menu.ant-menu-inline-collapsed .ant-menu-submenu-title {
          padding: 0 !important;
          justify-content: center !important;
          gap: 0 !important;
        }
        .qq-sidebar-menu.ant-menu-inline-collapsed .ant-menu-title-content,
        .qq-sidebar-menu.ant-menu-inline-collapsed .ant-menu-submenu-arrow {
          display: none !important;
        }
        .qq-sidebar-menu .ant-menu-item:hover,
        .qq-sidebar-menu .ant-menu-submenu-title:hover {
          background: rgba(255, 255, 255, 0.08) !important;
          color: #ffffff !important;
        }
        .qq-sidebar-menu .ant-menu-item-selected {
          background: linear-gradient(135deg, rgba(59, 130, 246, 0.45), rgba(37, 99, 235, 0.55)) !important;
          color: #ffffff !important;
          box-shadow: 0 6px 18px -8px rgba(59, 130, 246, 0.65), inset 0 1px 0 rgba(255,255,255,0.18);
        }
        .qq-sidebar-menu .ant-menu-item-selected::after {
          content: '';
          position: absolute;
          left: 0;
          top: 8px;
          bottom: 8px;
          width: 3px;
          border-radius: 0 4px 4px 0;
          background: linear-gradient(180deg, #93c5fd, #60a5fa);
        }
        .qq-sidebar-menu .ant-menu-submenu-arrow {
          color: rgba(226, 232, 240, 0.65) !important;
        }
        .qq-sidebar-menu::-webkit-scrollbar { width: 6px; }
        .qq-sidebar-menu::-webkit-scrollbar-thumb {
          background: rgba(255,255,255,0.12);
          border-radius: 3px;
        }
      `}</style>
    </Sider>
  );
};

export default Sidebar;
